import axios from "axios";
import { batch } from "react-redux";

import {
  FETCH_PRACTICE_CONTEXT,
  LOADING_PRACTICE_CONTEXT,
  FETCH_BOOKS,
  FETCH_UNITS,
  FETCH_SECTIONS,
  FETCH_LOGS,
  FETCH_PAGES_NUMBER,
  UPDATE_ACCOUNT
} from "./type";

/**
 * Fetch all practice context data in a single API call
 * This replaces multiple separate calls to:
 * - /api/book
 * - /api/unit
 * - /api/section
 * - /api/view (logs)
 * - /api/pageNumber
 * - /api/account
 */
export const fetchPracticeContext =
  (options, callback) => async (dispatch, getStore) => {
    const { booksetId, bookId } = options;

    if (!booksetId || !bookId) {
      console.error("fetchPracticeContext: Missing booksetId or bookId");
      if (callback) callback();
      return;
    }

    // Check if we already have all the data we need
    const skipIfLoaded = options?.skipIfLoaded !== false;
    if (skipIfLoaded) {
      const state = getStore();
      const { book, unit, section, log } = state;

      const hasBooks =
        Object.values(book?.bookMap || {}).filter(
          (b) => b._bookset === booksetId || b.booksetId === booksetId
        ).length > 0;

      const hasUnits =
        Object.values(unit?.unitMap || {}).filter((u) => u._book === bookId)
          .length > 0;

      const hasSections =
        Object.values(section?.sectionMap || {}).filter(
          (s) => s._book === bookId
        ).length > 0;

      const hasLogs =
        Object.values(log?.logMap || {}).filter(
          (l) => l.bookId === bookId || l._book === bookId
        ).length > 0;

      // If all data is already loaded, skip the API call
      if (hasBooks && hasUnits && hasSections && hasLogs) {
        if (callback) callback();
        return;
      }
    }

    dispatch({ type: LOADING_PRACTICE_CONTEXT, payload: {} });

    try {
      const {
        data: { bookMap, unitMap, sectionMap, logMap, pagesNumbers, account }
      } = await axios.get(
        `practice-context?booksetId=${booksetId}&bookId=${bookId}`
      );

      // Dispatch all the data to their respective reducers
      batch(() => {
        dispatch({ type: FETCH_BOOKS, payload: { bookMap } });
        dispatch({ type: FETCH_UNITS, payload: { unitMap } });
        dispatch({ type: FETCH_SECTIONS, payload: { sectionMap } });
        dispatch({ type: FETCH_LOGS, payload: { logMap } });
        dispatch({ type: FETCH_PAGES_NUMBER, payload: { pagesNumbers } });
        if (account) {
          dispatch({ type: UPDATE_ACCOUNT, payload: { account } });
        }
        dispatch({
          type: FETCH_PRACTICE_CONTEXT,
          payload: { booksetId, bookId }
        });
      });

      if (callback) callback();
    } catch (error) {
      console.error("fetchPracticeContext error:", error);
      if (callback) callback(error);
    }
  };
